/**
 * The shape the survey builder edits, and how it maps to the contract.
 *
 * The form keeps **every language at once**, not the language currently on screen: switching tabs
 * only changes which slice is rendered, so nothing typed is ever thrown away. The payloads are
 * assembled at save time — base columns in English, the rest as `translations`, exactly as
 * `ADMIN · Surveys` describes.
 *
 * Two identifiers are **not** translated and must not be: a question's `key` and an option's
 * `value`. They are written into every answer row, so they are one code shared by the three
 * languages, and renaming one after an answer exists splits its count in two with no error and no
 * warning (R5). The form holds them once, outside the language slices.
 *
 * @module Pages/BUs/Surveys
 */

import type {
  DelayAnchor,
  DelayUnit,
  MatrixRowType,
  OptInState,
  SaveQuestionsPayload,
  Survey,
  SurveyCreatePayload,
  SurveyDetail,
  SurveyLabelTranslation,
  SurveyLanguage,
  SurveyLayout,
  SurveyQuestion,
  SurveyQuestionConfig,
  SurveyQuestionInput,
  SurveyQuestionType,
  SurveyTranslation,
  SurveyTrigger,
  SurveyUpdatePayload,
} from '../Types/Surveys.types';
import { SURVEY_LANGUAGES } from '../Types/Surveys.types';

/** The base language: what fills the survey's own columns. Everything else is a translation. */
export const BASE_LANGUAGE: SurveyLanguage = 'en';

export type ByLanguage = Record<SurveyLanguage, string>;

export const emptyByLanguage = (): ByLanguage => ({ en: '', pt: '', es: '' });

/**
 * The textual half of `config`, which varies per language.
 *
 * Kept apart from the numbers on purpose: `min`, `max` and `maxLength` are the same in every
 * language, and duplicating them per tab would let the Portuguese scale run 1–5 while the Spanish
 * one runs 1–7 — a difference nobody would see until the results came back on two scales.
 */
export interface QuestionConfigTexts {
  /** SHORT_TEXT / LONG_TEXT */
  placeholder: string;
  /** NPS */
  leftLabel: string;
  rightLabel: string;
  /** BOOLEAN */
  trueLabel: string;
  falseLabel: string;
  /** RATING — the two ends of the scale. */
  minLabel: string;
  maxLabel: string;
}

export const EMPTY_CONFIG_TEXTS: QuestionConfigTexts = {
  placeholder: '',
  leftLabel: '',
  rightLabel: '',
  trueLabel: '',
  falseLabel: '',
  minLabel: '',
  maxLabel: '',
};

export const CONFIG_TEXT_FIELDS = Object.keys(EMPTY_CONFIG_TEXTS) as (keyof QuestionConfigTexts)[];

/** The numeric half of `config`. One value for the three languages. */
export interface QuestionConfigNumbers {
  min?: number;
  max?: number;
  maxLength?: number;
  minSelections?: number;
  maxSelections?: number;
}

export interface SurveyOptionDraft {
  /** Local identity, for React keys and drag handles. Never sent. */
  uid: string;
  /** Stable code written into every answer. One for the three languages. */
  value: string;
  labels: ByLanguage;
  isExclusive: boolean;
  allowsFreeText: boolean;
}

export interface SurveyRowDraft {
  uid: string;
  /** The row's id in the BFF, when it already exists. Proves its `key` did not move. */
  id?: string;
  key: string;
  /** Set once the key was typed by hand, which is when it stops following the label. */
  keyTouched: boolean;
  type: MatrixRowType;
  labels: ByLanguage;
  helpTexts: ByLanguage;
  isRequired: boolean;
}

export interface SurveyQuestionDraft {
  uid: string;
  /** The question's id in the BFF, when it already exists. */
  id?: string;
  key: string;
  keyTouched: boolean;
  type: SurveyQuestionType;
  step: number;
  isRequired: boolean;
  labels: ByLanguage;
  helpTexts: ByLanguage;
  numbers: QuestionConfigNumbers;
  configTexts: Record<SurveyLanguage, QuestionConfigTexts>;
  options: SurveyOptionDraft[];
  rows: SurveyRowDraft[];
}

export interface SurveyTextsDraft {
  title: string;
  description: string;
}

export const EMPTY_SURVEY_TEXTS: SurveyTextsDraft = { title: '', description: '' };

export interface SurveyFormData {
  tenantSlug: string;
  slug: string;
  trigger: SurveyTrigger;
  layout: SurveyLayout;
  priority: number;
  isActive: boolean;
  /** `datetime-local` strings, or empty for "no bound". */
  opensAt: string;
  closesAt: string;
  /** Empty means dismissing hides it for good. */
  snoozeDays: string;
  useCaseId: string;
  pageId: string;
  /** The checkbox that reveals the four eligibility fields. */
  conditionOnOptIn: boolean;
  requiresOptInFeatureId: string;
  requiredOptInState: OptInState;
  delayAmount: string;
  delayUnit: DelayUnit;
  delayAnchor: DelayAnchor;
  eligibleOptInFrom: string;
  maxDaysSinceAnchor: string;
  texts: Record<SurveyLanguage, SurveyTextsDraft>;
  questions: SurveyQuestionDraft[];
}

/** The BFF's own defaults when a survey hangs off an opt-in feature. */
export const DEFAULT_DELAY_AMOUNT = 7;
/**
 * Days, the same unit every delay meant before the unit existed. Minutes are for watching the rule
 * work — a survey registered in minutes by accident reaches everyone the moment it is saved.
 */
export const DEFAULT_DELAY_UNIT: DelayUnit = 'DAYS';
export const DEFAULT_DELAY_ANCHOR: DelayAnchor = 'LAST_ENABLED_AT';

let uidCounter = 0;

/** Monotonic, per session. Only ever used as a React key and a drag id. */
export const nextUid = (prefix: string): string => {
  uidCounter += 1;

  return `${prefix}_${uidCounter}`;
};

/**
 * The alphabet a question `key` and an option `value` live in.
 *
 * Lowercase, digits and underscore. It is the CSV column name, so a space or an accent there
 * turns into a column nobody can address in a spreadsheet formula.
 */
export const slugifyCode = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+/, '');

/** Survey slugs are hyphenated, like the opt-in key — `satisfacao-mdm`, never `satisfacao_mdm`. */
export const slugifySlug = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '');

export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * A code derived from a label, unique among `taken`.
 *
 * The suffix is `_2`, `_3`, … — the same rule the BFF applies when the client sends no key, so a
 * key generated here and one generated there cannot disagree.
 */
export const uniqueCode = (label: string, taken: string[], fallback = 'pergunta'): string => {
  const base = slugifyCode(label).slice(0, 60) || fallback;

  if (!taken.includes(base)) return base;

  let suffix = 2;
  while (taken.includes(`${base}_${suffix}`)) suffix += 1;

  return `${base}_${suffix}`;
};

export const createEmptyOption = (): SurveyOptionDraft => ({
  uid: nextUid('opt'),
  value: '',
  labels: emptyByLanguage(),
  isExclusive: false,
  allowsFreeText: false,
});

export const createEmptyRow = (): SurveyRowDraft => ({
  uid: nextUid('row'),
  key: '',
  keyTouched: false,
  type: 'SINGLE_CHOICE',
  labels: emptyByLanguage(),
  helpTexts: emptyByLanguage(),
  isRequired: true,
});

const emptyConfigTexts = (): Record<SurveyLanguage, QuestionConfigTexts> => ({
  en: { ...EMPTY_CONFIG_TEXTS },
  pt: { ...EMPTY_CONFIG_TEXTS },
  es: { ...EMPTY_CONFIG_TEXTS },
});

/**
 * The settings a type arrives with.
 *
 * A rating with no scale would render as 1–5 anyway, but leaving the numbers blank makes the
 * builder look like it has nothing to configure — so the defaults are written down where they can
 * be seen and changed.
 */
export const defaultNumbersFor = (type: SurveyQuestionType): QuestionConfigNumbers => {
  switch (type) {
    case 'RATING':
      return { min: 1, max: 5 };
    case 'SHORT_TEXT':
      return { maxLength: 120 };
    case 'LONG_TEXT':
      return { maxLength: 1000 };
    case 'MULTI_CHOICE':
      return { minSelections: 1 };
    default:
      return {};
  }
};

export const createEmptyQuestion = (step = 1): SurveyQuestionDraft => ({
  uid: nextUid('q'),
  key: '',
  keyTouched: false,
  type: 'SINGLE_CHOICE',
  step,
  isRequired: true,
  labels: emptyByLanguage(),
  helpTexts: emptyByLanguage(),
  numbers: defaultNumbersFor('SINGLE_CHOICE'),
  configTexts: emptyConfigTexts(),
  options: [createEmptyOption(), createEmptyOption()],
  rows: [],
});

export const createEmptyFormData = (tenantSlug = ''): SurveyFormData => ({
  tenantSlug,
  slug: '',
  trigger: 'AUTO',
  layout: 'ONE_PER_STEP',
  priority: 0,
  isActive: true,
  opensAt: '',
  closesAt: '',
  snoozeDays: '',
  useCaseId: '',
  pageId: '',
  conditionOnOptIn: false,
  requiresOptInFeatureId: '',
  requiredOptInState: 'ENABLED',
  delayAmount: String(DEFAULT_DELAY_AMOUNT),
  delayUnit: DEFAULT_DELAY_UNIT,
  delayAnchor: DEFAULT_DELAY_ANCHOR,
  eligibleOptInFrom: '',
  maxDaysSinceAnchor: '',
  texts: {
    en: { ...EMPTY_SURVEY_TEXTS },
    pt: { ...EMPTY_SURVEY_TEXTS },
    es: { ...EMPTY_SURVEY_TEXTS },
  },
  questions: [],
});

/* ─────────────────────────── ISO ↔ datetime-local ─────────────────────── */

/**
 * `2026-10-01T00:00:00.000Z` → `2026-10-01T00:00`, in the browser's own zone.
 *
 * The field is `datetime-local` because the person setting a delivery window thinks in their own
 * clock, not in UTC; the conversion back happens on save.
 */
export const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const localInputToIso = (value: string): string | undefined => {
  if (!value.trim()) return undefined;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toOptionalInt = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? Math.trunc(parsed) : undefined;
};

/* ──────────────────────────────── Payloads ────────────────────────────── */

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();

  return trimmed === '' ? undefined : trimmed;
};

/** Which config keys a type actually uses. Anything else would be noise in the payload. */
export const configFieldsFor = (
  type: SurveyQuestionType,
): { numbers: (keyof QuestionConfigNumbers)[]; texts: (keyof QuestionConfigTexts)[] } => {
  switch (type) {
    case 'RATING':
      return { numbers: ['min', 'max'], texts: ['minLabel', 'maxLabel'] };
    case 'NPS':
      return { numbers: [], texts: ['leftLabel', 'rightLabel'] };
    case 'BOOLEAN':
      return { numbers: [], texts: ['trueLabel', 'falseLabel'] };
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
      return { numbers: ['maxLength'], texts: ['placeholder'] };
    case 'MULTI_CHOICE':
      return { numbers: ['minSelections', 'maxSelections'], texts: [] };
    default:
      return { numbers: [], texts: [] };
  }
};

/**
 * `config` for one language.
 *
 * The base row carries the numbers plus the English texts; a translation carries only the texts
 * of its own language, which is what `PUT /:id/questions` documents and what keeps a scale from
 * being 1–5 in one language and 1–7 in another.
 */
export const buildConfig = (
  question: SurveyQuestionDraft,
  language: SurveyLanguage,
): SurveyQuestionConfig => {
  const fields = configFieldsFor(question.type);
  const config: SurveyQuestionConfig = {};

  if (language === BASE_LANGUAGE) {
    fields.numbers.forEach(field => {
      const value = question.numbers[field];
      if (value !== undefined) config[field] = value;
    });
  }

  const texts = question.configTexts[language];
  const scaleLabels: Record<string, string> = {};

  fields.texts.forEach(field => {
    const value = texts[field].trim();
    if (value === '') return;

    if (field === 'minLabel') {
      scaleLabels[String(question.numbers.min ?? 1)] = value;

      return;
    }

    if (field === 'maxLabel') {
      scaleLabels[String(question.numbers.max ?? 5)] = value;

      return;
    }

    config[field] = value;
  });

  if (Object.keys(scaleLabels).length > 0) config.labels = scaleLabels;

  return config;
};

const optionInputs = (question: SurveyQuestionDraft) =>
  question.options
    .filter(option => option.value.trim() !== '')
    .map((option, index) => ({
      value: option.value.trim(),
      label: option.labels[BASE_LANGUAGE].trim(),
      order: index,
      isExclusive: option.isExclusive,
      allowsFreeText: option.allowsFreeText,
      translations: SURVEY_LANGUAGES.filter(language => language !== BASE_LANGUAGE)
        .filter(language => option.labels[language].trim() !== '')
        .map(language => ({ language, label: option.labels[language].trim() })),
    }));

const questionTranslations = (question: SurveyQuestionDraft): SurveyLabelTranslation[] =>
  SURVEY_LANGUAGES.filter(language => language !== BASE_LANGUAGE)
    .map((language): SurveyLabelTranslation | null => {
      const label = question.labels[language].trim();
      const helpText = question.helpTexts[language].trim();
      const config = buildConfig(question, language);

      if (label === '' && helpText === '' && Object.keys(config).length === 0) return null;

      return {
        language,
        label,
        ...(helpText ? { helpText } : {}),
        ...(Object.keys(config).length > 0 ? { config } : {}),
      };
    })
    .filter((translation): translation is SurveyLabelTranslation => translation !== null);

const rowTranslations = (row: SurveyRowDraft): SurveyLabelTranslation[] =>
  SURVEY_LANGUAGES.filter(language => language !== BASE_LANGUAGE)
    .map((language): SurveyLabelTranslation | null => {
      const label = row.labels[language].trim();
      const helpText = row.helpTexts[language].trim();

      if (label === '' && helpText === '') return null;

      return { language, label, ...(helpText ? { helpText } : {}) };
    })
    .filter((translation): translation is SurveyLabelTranslation => translation !== null);

/**
 * The questionnaire, ordered.
 *
 * `order` is recomputed from the position inside the step rather than stored: a drag that moved a
 * question is a change of position, and keeping a separate number in sync with it is how two
 * questions end up claiming order 3.
 */
export const buildQuestionsPayload = (form: SurveyFormData): SaveQuestionsPayload => {
  const orderByStep = new Map<number, number>();

  const questions: SurveyQuestionInput[] = form.questions.map(question => {
    const order = orderByStep.get(question.step) ?? 0;
    orderByStep.set(question.step, order + 1);

    const carriesOptions =
      question.type === 'SINGLE_CHOICE' ||
      question.type === 'MULTI_CHOICE' ||
      question.type === 'MATRIX';

    return {
      ...(question.id ? { id: question.id } : {}),
      key: question.key.trim(),
      type: question.type,
      label: question.labels[BASE_LANGUAGE].trim(),
      ...(question.helpTexts[BASE_LANGUAGE].trim()
        ? { helpText: question.helpTexts[BASE_LANGUAGE].trim() }
        : {}),
      isRequired: question.isRequired,
      step: question.step,
      order,
      config: buildConfig(question, BASE_LANGUAGE),
      ...(carriesOptions ? { options: optionInputs(question) } : {}),
      ...(question.type === 'MATRIX'
        ? {
            rows: question.rows
              .filter(row => row.key.trim() !== '')
              .map((row, index) => ({
                ...(row.id ? { id: row.id } : {}),
                key: row.key.trim(),
                type: row.type,
                label: row.labels[BASE_LANGUAGE].trim(),
                ...(row.helpTexts[BASE_LANGUAGE].trim()
                  ? { helpText: row.helpTexts[BASE_LANGUAGE].trim() }
                  : {}),
                isRequired: row.isRequired,
                order: index,
                translations: rowTranslations(row),
              })),
          }
        : {}),
      translations: questionTranslations(question),
    };
  });

  return { questions };
};

export const buildSurveyTranslations = (form: SurveyFormData): SurveyTranslation[] =>
  SURVEY_LANGUAGES.filter(language => language !== BASE_LANGUAGE)
    .filter(language => form.texts[language].title.trim() !== '')
    .map(language => ({
      language,
      title: form.texts[language].title.trim(),
      ...(form.texts[language].description.trim()
        ? { description: form.texts[language].description.trim() }
        : {}),
    }));

/**
 * The eligibility half of the payload.
 *
 * Three shapes, not two, because the exit questionnaire is conditioned on a feature without being
 * *delivered* by that condition. For `ON_OPT_OUT` the BFF checks one thing — that the feature is
 * alive — and ignores the delay, the anchor and the required state entirely, so sending a delay
 * would write a rule into the database that nothing reads and that the next person would believe.
 * `DISABLED` does travel, because it is true by definition: the person is leaving as they answer.
 */
const buildEligibility = (
  form: SurveyFormData,
): Pick<
  SurveyCreatePayload,
  | 'requiresOptInFeatureId'
  | 'requiredOptInState'
  | 'delayAmount'
  | 'delayUnit'
  | 'delayAnchor'
  | 'eligibleOptInFrom'
  | 'maxDaysSinceAnchor'
> => {
  if (!form.requiresOptInFeatureId) return {};

  if (form.trigger === 'ON_OPT_OUT') {
    return {
      requiresOptInFeatureId: form.requiresOptInFeatureId,
      requiredOptInState: 'DISABLED',
    };
  }

  if (!form.conditionOnOptIn) return {};

  return {
    requiresOptInFeatureId: form.requiresOptInFeatureId,
    requiredOptInState: form.requiredOptInState,
    // Always the pair. The BFF reads a missing unit as DAYS and refuses nothing, so an
    // amount sent on its own is a silent unit change rather than an error.
    delayAmount: toOptionalInt(form.delayAmount) ?? DEFAULT_DELAY_AMOUNT,
    delayUnit: form.delayUnit,
    delayAnchor: form.delayAnchor,
    ...(localInputToIso(form.eligibleOptInFrom)
      ? { eligibleOptInFrom: localInputToIso(form.eligibleOptInFrom) }
      : {}),
    ...(toOptionalInt(form.maxDaysSinceAnchor) !== undefined
      ? { maxDaysSinceAnchor: toOptionalInt(form.maxDaysSinceAnchor) }
      : {}),
  };
};

/**
 * The survey shell.
 *
 * Eligibility only travels when the checkbox is on. When it is off the four fields are sent as
 * `undefined` on create and explicitly cleared on update — an unconditional survey that kept a
 * stale `requiresOptInFeatureId` would quietly keep being conditional (R11's mirror image).
 */
export const buildCreatePayload = (form: SurveyFormData): SurveyCreatePayload => ({
  tenantSlug: form.tenantSlug,
  slug: form.slug.trim(),
  title: form.texts[BASE_LANGUAGE].title.trim(),
  ...(trimmedOrUndefined(form.texts[BASE_LANGUAGE].description)
    ? { description: form.texts[BASE_LANGUAGE].description.trim() }
    : {}),
  layout: form.layout,
  trigger: form.trigger,
  priority: form.priority,
  isActive: form.isActive,
  ...(localInputToIso(form.opensAt) ? { opensAt: localInputToIso(form.opensAt) } : {}),
  ...(localInputToIso(form.closesAt) ? { closesAt: localInputToIso(form.closesAt) } : {}),
  ...(toOptionalInt(form.snoozeDays) !== undefined
    ? { snoozeDays: toOptionalInt(form.snoozeDays) }
    : {}),
  ...(form.useCaseId ? { useCaseId: form.useCaseId } : {}),
  ...(form.pageId ? { pageId: form.pageId } : {}),
  ...buildEligibility(form),
  translations: buildSurveyTranslations(form),
});

/**
 * The same payload for an edit, minus `tenantSlug`.
 *
 * Every optional field is sent even when empty, because `PATCH` treats an absent key as "leave it
 * alone": closing a delivery window or dropping the opt-in condition has to be expressible, and
 * omitting the field is indistinguishable from not having touched it.
 */
export const buildUpdatePayload = (form: SurveyFormData): SurveyUpdatePayload => {
  const created = buildCreatePayload(form);
  const { tenantSlug: _tenantSlug, ...rest } = created;

  return {
    ...rest,
    description: form.texts[BASE_LANGUAGE].description.trim(),
    opensAt: localInputToIso(form.opensAt),
    closesAt: localInputToIso(form.closesAt),
    snoozeDays: toOptionalInt(form.snoozeDays),
    useCaseId: form.useCaseId || undefined,
    pageId: form.pageId || undefined,
    /*
      Whatever eligibility did not travel is explicitly absent, so dropping the opt-in condition —
      or turning a survey into an exit questionnaire, which has no delay — cannot leave a stale
      rule behind (R11's mirror image).
    */
    requiresOptInFeatureId: undefined,
    requiredOptInState: undefined,
    delayAmount: undefined,
    delayUnit: undefined,
    delayAnchor: undefined,
    eligibleOptInFrom: undefined,
    maxDaysSinceAnchor: undefined,
    ...buildEligibility(form),
  };
};

/* ────────────────────────────── Survey → form ─────────────────────────── */

const readTranslations = (
  translations: SurveyLabelTranslation[] | undefined,
): Map<string, SurveyLabelTranslation> =>
  new Map((translations ?? []).map(translation => [translation.language, translation]));

const configTextsFrom = (
  config: SurveyQuestionConfig | undefined,
  min: number | undefined,
  max: number | undefined,
): QuestionConfigTexts => {
  const labels = config?.labels ?? {};

  return {
    placeholder: config?.placeholder ?? '',
    leftLabel: config?.leftLabel ?? '',
    rightLabel: config?.rightLabel ?? '',
    trueLabel: config?.trueLabel ?? '',
    falseLabel: config?.falseLabel ?? '',
    minLabel: labels[String(min ?? 1)] ?? '',
    maxLabel: labels[String(max ?? 5)] ?? '',
  };
};

const mapQuestion = (question: SurveyQuestion): SurveyQuestionDraft => {
  const byLanguage = readTranslations(question.translations);
  const numbers: QuestionConfigNumbers = {
    min: question.config?.min,
    max: question.config?.max,
    maxLength: question.config?.maxLength,
    minSelections: question.config?.minSelections,
    maxSelections: question.config?.maxSelections,
  };

  const labels = emptyByLanguage();
  const helpTexts = emptyByLanguage();
  const configTexts = emptyConfigTexts();

  labels[BASE_LANGUAGE] = question.label ?? '';
  helpTexts[BASE_LANGUAGE] = question.helpText ?? '';
  configTexts[BASE_LANGUAGE] = configTextsFrom(question.config, numbers.min, numbers.max);

  SURVEY_LANGUAGES.filter(language => language !== BASE_LANGUAGE).forEach(language => {
    const translation = byLanguage.get(language);
    labels[language] = translation?.label ?? '';
    helpTexts[language] = translation?.helpText ?? '';
    configTexts[language] = configTextsFrom(translation?.config, numbers.min, numbers.max);
  });

  return {
    uid: nextUid('q'),
    id: question.id,
    key: question.key,
    // A key that came back from the BFF is settled: it must not start following the label again
    // the moment someone fixes a typo in it.
    keyTouched: true,
    type: question.type,
    step: question.step,
    isRequired: question.isRequired,
    labels,
    helpTexts,
    numbers,
    configTexts,
    options: question.options.map(option => {
      const optionLabels = emptyByLanguage();
      optionLabels[BASE_LANGUAGE] = option.label;

      readTranslations(option.translations).forEach((translation, language) => {
        if (language === BASE_LANGUAGE) return;
        optionLabels[language as SurveyLanguage] = translation.label;
      });

      return {
        uid: nextUid('opt'),
        value: option.value,
        labels: optionLabels,
        isExclusive: option.isExclusive,
        allowsFreeText: option.allowsFreeText,
      };
    }),
    rows: question.rows.map(row => {
      const rowLabels = emptyByLanguage();
      const rowHelp = emptyByLanguage();
      rowLabels[BASE_LANGUAGE] = row.label;
      rowHelp[BASE_LANGUAGE] = row.helpText ?? '';

      readTranslations(row.translations).forEach((translation, language) => {
        if (language === BASE_LANGUAGE) return;
        rowLabels[language as SurveyLanguage] = translation.label;
        rowHelp[language as SurveyLanguage] = translation.helpText ?? '';
      });

      return {
        uid: nextUid('row'),
        id: row.id,
        key: row.key,
        keyTouched: true,
        type: row.type,
        labels: rowLabels,
        helpTexts: rowHelp,
        isRequired: row.isRequired,
      };
    }),
  };
};

/** Seeds the form from an existing survey, spreading its translations back into the tabs. */
export const mapSurveyToFormData = (survey: SurveyDetail): SurveyFormData => {
  const texts: Record<SurveyLanguage, SurveyTextsDraft> = {
    en: { title: survey.title ?? '', description: survey.description ?? '' },
    pt: { ...EMPTY_SURVEY_TEXTS },
    es: { ...EMPTY_SURVEY_TEXTS },
  };

  (survey.translations ?? []).forEach(translation => {
    if (translation.language === BASE_LANGUAGE) return;
    if (!SURVEY_LANGUAGES.includes(translation.language as SurveyLanguage)) return;

    texts[translation.language as SurveyLanguage] = {
      title: translation.title ?? '',
      description: translation.description ?? '',
    };
  });

  return {
    tenantSlug: survey.tenantSlug,
    slug: survey.slug,
    trigger: survey.trigger,
    layout: survey.layout,
    priority: survey.priority,
    isActive: survey.isActive,
    opensAt: isoToLocalInput(survey.opensAt),
    closesAt: isoToLocalInput(survey.closesAt),
    snoozeDays: survey.snoozeDays === null ? '' : String(survey.snoozeDays),
    useCaseId: survey.useCase?.id ?? '',
    pageId: survey.page?.id ?? '',
    conditionOnOptIn: survey.requiresOptInFeature !== null,
    requiresOptInFeatureId: survey.requiresOptInFeature?.id ?? '',
    requiredOptInState: survey.requiredOptInState ?? 'ENABLED',
    delayAmount:
      survey.delayAmount === null ? String(DEFAULT_DELAY_AMOUNT) : String(survey.delayAmount),
    delayUnit: survey.delayUnit ?? DEFAULT_DELAY_UNIT,
    delayAnchor: survey.delayAnchor ?? DEFAULT_DELAY_ANCHOR,
    eligibleOptInFrom: isoToLocalInput(survey.eligibleOptInFrom),
    maxDaysSinceAnchor: survey.maxDaysSinceAnchor === null ? '' : String(survey.maxDaysSinceAnchor),
    texts,
    questions: [...(survey.questions ?? [])]
      .sort((a, b) => a.step - b.step || a.order - b.order)
      .map(question => mapQuestion(question)),
  };
};

/* ─────────────────────────────── Validation ───────────────────────────── */

export interface SurveyFormErrors {
  tenantSlug?: string;
  slug?: string;
  title?: string;
  optInFeature?: string;
  questions?: string;
}

/**
 * What must be true before the BFF is asked.
 *
 * Only the base language is checked here: the missing-translation gate is the pendências panel,
 * which blocks the save button on its own (R6b), and duplicating it would report the same gap
 * twice in two different voices.
 */
export const validateFormData = (form: SurveyFormData): SurveyFormErrors => {
  const errors: SurveyFormErrors = {};

  if (!form.tenantSlug) errors.tenantSlug = 'Escolha a BU dona da pesquisa.';

  if (!form.slug.trim()) {
    errors.slug = 'O slug é obrigatório — é ele que o purge pede de volta por escrito.';
  } else if (!SLUG_PATTERN.test(form.slug.trim())) {
    errors.slug = 'Use minúsculas, números e hífen simples (ex.: satisfacao-mdm).';
  }

  if (!form.texts[BASE_LANGUAGE].title.trim()) {
    errors.title = 'O título em inglês é obrigatório: é a coluna base da tabela.';
  }

  if (form.conditionOnOptIn && !form.requiresOptInFeatureId) {
    errors.optInFeature = 'Escolha a funcionalidade opt-in que condiciona a pesquisa.';
  }

  /*
    An `ON_OPT_OUT` survey with no feature is refused by the BFF with a `400`, and the reason is
    worth repeating here rather than letting the request carry it: this trigger is out of the
    automatic delivery (R34), so the feature's `exitSurveyId` is the ONLY route to it. Without one
    the survey would be approved, active, inside its window — and asked to nobody, silently.
  */
  if (form.trigger === 'ON_OPT_OUT' && !form.requiresOptInFeatureId) {
    errors.optInFeature =
      'Um questionário de saída é o questionário de UMA funcionalidade opt-in, e precisa nomeá-la: sem isso ele não tem como chegar a ninguém.';
  }

  const keys = form.questions.map(question => question.key.trim());

  if (keys.some(key => key === '')) {
    errors.questions = 'Toda pergunta precisa de um identificador.';
  } else if (new Set(keys).size !== keys.length) {
    errors.questions = 'Há duas perguntas com o mesmo identificador.';
  }

  return errors;
};

export const hasErrors = (errors: SurveyFormErrors): boolean => Object.keys(errors).length > 0;

/* ────────────────────── Contradição de segmentação ─────────────────────── */

export interface TargetingUseCase {
  id: string;
  name: string;
}

/** An opt-in feature, reduced to what targeting needs to know about it. */
export interface TargetingFeature {
  id: string;
  name: string;
  /** **Empty means every use case of the BU**, never none — an absent restriction is no restriction. */
  useCases: TargetingUseCase[];
}

export interface UseCaseConflict {
  featureName: string;
  /** Who the feature is actually offered to. */
  featureUseCases: TargetingUseCase[];
  /** The use case the survey is aimed at, which is not one of them. */
  useCaseName: string;
}

const featureOf = (
  form: SurveyFormData,
  features: TargetingFeature[],
): TargetingFeature | undefined =>
  form.conditionOnOptIn && form.requiresOptInFeatureId
    ? features.find(feature => feature.id === form.requiresOptInFeatureId)
    : undefined;

/**
 * Which use cases the survey may still be narrowed to.
 *
 * With an opt-in condition the audience is **already** limited to whoever could turn the feature
 * on. The survey's own use case exists to narrow inside that, never to widen it, so anything
 * outside the feature's set is not a risky configuration — it is a meaningless one. `null` means
 * "no restriction": either there is no condition, or the feature is offered to everyone.
 */
export const allowedUseCaseIds = (
  form: SurveyFormData,
  features: TargetingFeature[],
): string[] | null => {
  const feature = featureOf(form, features);

  if (!feature || feature.useCases.length === 0) return null;

  return feature.useCases.map(useCase => useCase.id);
};

/**
 * The configuration that reaches nobody, and gives no sign of it.
 *
 * The survey's use case and the feature's are joined with **AND**. Aim a survey at Água and hang it
 * off a feature only offered to Gás, and the intersection is empty: the survey is approved, active,
 * inside its window, and never reaches a single person. No error, no warning, and a results screen
 * that is blank without saying why — the kind of defect found weeks later, by asking why nobody
 * answered.
 *
 * The BFF is right and does not change: empty is empty. It is this screen's job to refuse to build
 * the contradiction.
 */
export const findUseCaseConflict = (
  form: SurveyFormData,
  features: TargetingFeature[],
  tenantUseCases: TargetingUseCase[],
): UseCaseConflict | null => {
  if (!form.useCaseId) return null;

  const feature = featureOf(form, features);

  if (!feature || feature.useCases.length === 0) return null;
  if (feature.useCases.some(useCase => useCase.id === form.useCaseId)) return null;

  const chosen = tenantUseCases.find(useCase => useCase.id === form.useCaseId);

  return {
    featureName: feature.name,
    featureUseCases: feature.useCases,
    useCaseName: chosen?.name ?? form.useCaseId,
  };
};

/* ─────────────────────────────── Questions ────────────────────────────── */

/** Every code already in use in the questionnaire — question keys and grid-row keys together. */
export const takenKeys = (questions: SurveyQuestionDraft[], exceptUid?: string): string[] => [
  ...questions
    .filter(question => question.uid !== exceptUid)
    .map(question => question.key.trim())
    .filter(Boolean),
  ...questions.flatMap(question =>
    question.rows
      .filter(row => row.uid !== exceptUid)
      .map(row => row.key.trim())
      .filter(Boolean),
  ),
];

/** The steps in use, in order. A questionnaire with no questions still has step 1. */
export const stepsOf = (questions: SurveyQuestionDraft[]): number[] => {
  const steps = [...new Set(questions.map(question => question.step))].sort((a, b) => a - b);

  return steps.length > 0 ? steps : [1];
};

/**
 * What a collapsed step says about itself.
 *
 * "3 perguntas" is the one thing the reader already knows — they collapsed it. What lets someone
 * recognise a step without opening it is **what is inside**, so the summary lists the prompts in
 * order, each cut short enough that three of them still fit on the header line.
 *
 * A question with no prompt yet in the open language shows its `key` instead of an empty gap: the
 * point is recognising the step, and a blank reads as "nothing here".
 */
export const stepSummary = (
  questions: SurveyQuestionDraft[],
  language: SurveyLanguage,
  maxLength = 34,
): string => {
  if (questions.length === 0) return 'passo vazio';

  return questions
    .map(question => {
      const label = question.labels[language].trim() || question.key.trim() || 'sem enunciado';

      return label.length > maxLength ? `${label.slice(0, maxLength - 1).trimEnd()}…` : label;
    })
    .join(' · ');
};

/** Questions of one step, in the order they were arranged. */
export const questionsOfStep = (
  questions: SurveyQuestionDraft[],
  step: number,
): SurveyQuestionDraft[] => questions.filter(question => question.step === step);

/**
 * Whether this question's identifiers are frozen.
 *
 * Frozen once the survey has answers **and** the question already existed when the form opened:
 * a question added right now has no answers of its own, so its key is still free.
 */
export const isQuestionFrozen = (question: SurveyQuestionDraft, responseCount: number): boolean =>
  responseCount > 0 && question.id !== undefined;

/** How much of the questionnaire a language has, for the badge on each tab. */
export const countTranslatedLabels = (
  form: SurveyFormData,
  language: SurveyLanguage,
): { filled: number; total: number } => {
  let filled = form.texts[language].title.trim() !== '' ? 1 : 0;
  let total = 1;

  form.questions.forEach(question => {
    total += 1;
    if (question.labels[language].trim() !== '') filled += 1;

    question.options.forEach(option => {
      total += 1;
      if (option.labels[language].trim() !== '') filled += 1;
    });

    question.rows.forEach(row => {
      total += 1;
      if (row.labels[language].trim() !== '') filled += 1;
    });
  });

  return { filled, total };
};

/** Whether the questionnaire changed in a way that needs a new version on an answered survey. */
export const questionnaireSignature = (form: SurveyFormData): string =>
  JSON.stringify(buildQuestionsPayload(form));

/** Names the delivered version in the banner of a survey being edited as a draft. */
export const liveVersionOf = (survey: Survey): number => survey.version;
